import { KatalComponent } from '../../shared/base';

/**
 * @component {kat-statustracker} KatalStatustracker Status tracker is used at a order or product level to given a visual representation to the user of progress types and errors.
 * @status Production
 * @guideline Do Use to track progress across a linear set of steps
 * @guideline Dont Use for cases where there is a large, non-linear set of steps
 * @example HealthyMix {"steps": ["complete", "complete", "error", "complete", "todo"]}
 */
export class KatalStatustracker extends KatalComponent {
  static get observedAttributes() {
    return ['steps'];
  }

  constructor() {
    super(KatalStatustracker.observedAttributes);
    this._steps = [];
  }

  connectedCallback() {
    super.connectedCallback();
    this.initialize();
    this.render();
  }

  initialize() {
    let stepsAttr = this.getAttribute('steps');
    if (stepsAttr) {
      this.steps = stepsAttr;
    }
  }

  /**
   * @param {json} steps an array of states to display the linear steps, in desired order. Valid states are "complete", "error", or "todo"
   * @required
   */
  get steps() {
    return this._steps || [];
  }

  set steps(value) {
    if (Array.isArray(value)) {
      this._steps = value;
      this.render();
    } else if (typeof value === 'string') {
      try {
        this.steps = value === '' ? [] : JSON.parse(value);
        this.render();
      } catch (err) {
        throw new Error(
          'KatalStatustracker - Items must be set as valid JSON - ' + err
        );
      }
    } else {
      //null or empty
      this._steps = [];
    }
  }

  getClassFromStepValue(stepValue) {
    if (stepValue === 'complete') {
      return 'complete';
    } else if (stepValue === 'error') {
      return 'error';
    } else {
      return 'todo';
    }
  }

  render() {
    const steps = this._steps;
    const lineLength = 20;
    const stepdotRadius = 2.5;
    const xOffset = 5;
    const yOffset = 5;
    const componentHeight = 10;

    let componentWidth = Math.max(
      0,
      xOffset + (steps.length - 1) * lineLength + xOffset
    );

    let lines = '';
    for (let i = 1; i < steps.length; i++) {
      let lineOriginX = xOffset + (i - 1) * lineLength;
      let lineDestinationX = xOffset + i * lineLength;
      lines += `<line x1="${lineOriginX}" y1="${yOffset}" x2="${lineDestinationX}" y2="${yOffset}" class="kat-st-edge ${this.getClassFromStepValue(
        steps[i]
      )}" />`;
    }

    let points = '';
    for (let i = 0; i < steps.length; i++) {
      let stepX = xOffset + i * lineLength;
      points += `<circle cx="${stepX}" cy="${yOffset}" r="${stepdotRadius}" class="kat-st-step ${this.getClassFromStepValue(
        steps[i]
      )}" />`;
    }

    this.innerHTML = `
            <div class="kat-st-container">
                <svg height="${componentHeight}" width="${componentWidth}">
                    <g class="kat-st-edges">${lines}</g>
                    <g class="kat-st-steps">${points}</g>
                </svg>
            </div>
        `;
  }

  // Respond to attribute changes.
  attributeChangedCallback() {
    this.render();
    this.dispatchCustomEvent('changed', { value: this.value }, false);
  }
}

KatalComponent.register('kat-statustracker', KatalStatustracker);
